import { useCallback } from "react";
import { Advert, AdvertStats } from "../types/Adverts";
import { HttpError } from "../types/Error";
import { PagedResults } from "../types/PagedResults";
import useApiClient from "./ApiClient";

const useAdvertsService = () => {
  const {
    get,
    post,
    postFormData,
    putFormData,
    put,
    delete: del,
  } = useApiClient();

  const getAdvert = async (id: string) => {
    try {
      const result = await get(`/admin/adverts/${id}`);
      return result;
    } catch (error) {
      console.error("Error fetching advert", error);
      throw error;
    }
  };

  const searchAdverts = useCallback(
    async (
      searchTerm: string,
      page: number = 1,
      itemsPerPage: number = 50
    ): Promise<PagedResults<Advert>> => {
      try {
        const results = await post<PagedResults<Advert>>(
          "/admin/adverts/search",
          { searchTerm, page, itemsPerPage }
        );
        // Check if the response is an error.
        if ((results as HttpError).errorCode) {
          throw (results as HttpError).description;
        }

        return results as PagedResults<Advert>;
      } catch (error) {
        console.error("Error searching adverts", error);
        throw error;
      }
    },
    [post]
  );

  const createAdvert = useCallback(
    async (advertData: FormData): Promise<Advert | HttpError> => {
      try {
        const result = await postFormData<Advert>(
          "/admin/adverts",
          advertData
        );
        return result;
      } catch (error) {
        console.error("Error creating advert", error);
        throw error;
      }
    },
    [postFormData]
  );

  const updateAdvert = useCallback(
      async (id: string, advertData: FormData): Promise<Advert | HttpError> => {
        try {
          const result = await putFormData<Advert>(
            `/admin/adverts/${id}`,
            advertData
          );
          return result;
        } catch (error) {
          console.error("Error updating advert", error);
          throw error;
        }
      },
      [putFormData]
    );

  const deleteAdvert = useCallback(
    async (id: string): Promise<boolean | HttpError> => {
      try {
        await del(`/admin/adverts/${id}`);
        return true;
      } catch (error) {
        console.error("Error deleting advert", error);
        throw error;
      }
    },
    [del]
  );

 const getAdvertStats = async (id: string): Promise<AdvertStats | HttpError> => {
    try {
      const result = await get(`/admin/adverts/${id}/stats`);
      return result;
    } catch (error) {
      console.error("Error fetching advert stats", error);
      throw error;
    }
  }; 

  return { getAdvert, searchAdverts, createAdvert, updateAdvert, deleteAdvert, getAdvertStats };
};

export default useAdvertsService;
